import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchBlogs } from "../services/api";
import { Footer } from "../components/common/SharedSections";
import { applySeo, resetSeo } from "../utils/seo";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";

const styles = `
  .blog-page {
    max-width: 1150px;
    margin: 0 auto;
    padding: 110px 20px 60px;
    min-height: 70vh;
  }
  .blog-page-title {
    font-family: "Syne", sans-serif;
    font-size: clamp(26px, 4vw, 38px);
    font-weight: 800;
    color: #1a1a2e;
    margin-bottom: 8px;
  }
  .blog-page-sub {
    color: #6b7280;
    font-size: 15px;
    margin-bottom: 34px;
  }
  .blog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
  }
  .blog-card {
    background: #fff;
    border: 1px solid #e8eaf2;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    display: flex;
    flex-direction: column;
  }
  .blog-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(30, 30, 80, 0.12);
  }
  .blog-card-img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    background: linear-gradient(135deg, #eef0fb, #e2e6f7);
    display: block;
  }
  .blog-card-img-placeholder {
    width: 100%;
    height: 180px;
    background: linear-gradient(135deg, #6246ea22, #10b98122);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 42px;
  }
  .blog-card-body { padding: 18px 20px 22px; display: flex; flex-direction: column; flex: 1; }
  .blog-card-cat {
    display: inline-block;
    align-self: flex-start;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #6246ea;
    background: #6246ea14;
    padding: 4px 10px;
    border-radius: 20px;
    margin-bottom: 10px;
  }
  .blog-card-title {
    font-family: "Syne", sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: #1a1a2e;
    line-height: 1.35;
    margin-bottom: 8px;
  }
  .blog-card-desc {
    font-size: 13.5px;
    color: #6b7280;
    line-height: 1.55;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .blog-card-date { margin-top: 14px; font-size: 12px; color: #9ca3af; }
  .blog-empty {
    text-align: center;
    padding: 80px 20px;
    color: #9ca3af;
    font-size: 15px;
  }
`;

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applySeo({
      title: "SkillYer Blog — Courses, Careers & Skill Guides",
      description:
        "Guides on course fees, durations, career scope and skills — researched and updated by SkillYer.",
      canonical: "https://skillyer.com/blog",
    });
    return resetSeo;
  }, []);

  useEffect(() => {
    fetchBlogs()
      .then((res) => setBlogs(res.data.data || []))
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <>
      <style>{styles}</style>
      <div className="blog-page">
        <h1 className="blog-page-title">SkillYer Blog</h1>
        <p className="blog-page-sub">
          Course guides, fees breakdowns, career scope and skill advice.
        </p>

        {loading ? (
          <div className="blog-empty">Loading articles…</div>
        ) : blogs.length === 0 ? (
          <div className="blog-empty">No articles published yet. Check back soon!</div>
        ) : (
          <div className="blog-grid">
            {blogs.map((b) => (
              <article
                key={b.id}
                className="blog-card"
                onClick={() => navigate(`/blog/${b.slug}`)}
              >
                {b.headImage ? (
                  <img
                    className="blog-card-img"
                    src={`${SERVER_URL}${b.headImage}`}
                    alt={b.headImageAlt || b.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="blog-card-img-placeholder">📝</div>
                )}
                <div className="blog-card-body">
                  <span className="blog-card-cat">{b.category}</span>
                  <h2 className="blog-card-title">{b.title}</h2>
                  <p className="blog-card-desc">{b.metaDescription}</p>
                  <div className="blog-card-date">{fmtDate(b.createdAt)}</div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default BlogPage;
